import {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import {
  Location, NavigateFunction, useLocation, useNavigate, useNavigationType,
} from 'react-router';

import {
  MY_STUDIES_FILTER_PARAMS,
  applyFilterChange,
  clearFilters as clearedFilters,
  defaultFilters,
  hasActiveFilters as filtersAreActive,
  hasFilterParams,
  parseFilters,
  ParseFiltersOptions,
  sanitizeFilterChange,
  serializeFilters,
} from 'helper/flux/myStudiesFilters';
import { todayInSaoPaulo } from 'helper/flux/myStudiesPeriod';
import { StudyViewer, isValidUserId } from 'helper/flux/myStudyRow';
import { DateOnly } from 'types/Flux/Personalized/myStudies';
import {
  MyStudiesCreatorUser,
  MyStudiesFilters,
  MyStudiesFiltersChange,
  SetFiltersOptions,
} from 'types/Flux/Personalized/myStudiesFilters';

/** Espera depois da última tecla antes de aplicar a busca (.dc.html:616). */
export const SEARCH_DEBOUNCE_MS = 450;

const STORAGE_PREFIX = 'flux-meus-estudos-filtros-v1';

/**
 * Chave da sessão, uma por usuário: estado salvo de outro usuário nunca é lido.
 *
 * @param userId - Id do usuário logado.
 * @param prefix - Prefixo da chave, um por produto (padrão: o do Personalizado).
 */
export function myStudiesFiltersStorageKey(
  userId: number,
  prefix: string = STORAGE_PREFIX,
): string {
  return `${prefix}:${userId}`;
}

/** Criador com nome conhecido (escolhido na tela); a URL só leva o id. */
type NamedCreator = Required<MyStudiesCreatorUser>;

/** O que fica na sessão: a URL canônica dos filtros e, se houver, o nome do criador escolhido. */
interface StoredFilters {
  query: string;
  creator: NamedCreator | null;
}

type HistoryMode = 'replace' | 'push';

/** Mudança que espera o debounce da busca (sempre com `busca`) e o timer que a aplica. */
interface PendingSearch {
  change: MyStudiesFiltersChange;
  timer: ReturnType<typeof setTimeout>;
}

/** Estado do último render, para o timer da busca e para mudanças seguidas no mesmo evento. */
interface LatestState {
  filters: MyStudiesFilters;
  location: Location;
  navigate: NavigateFunction;
}

/** Quem vê a tela e o que ela libera. */
export interface UseMyStudiesFiltersOptions extends StudyViewer, ParseFiltersOptions {
  /** Prefixo da chave de sessão, um por produto (padrão: o do Personalizado). */
  storagePrefix?: string;
}

export interface UseMyStudiesFiltersResult {
  /** Filtros aplicados: o que vai para `toQuery`, para os chips e para a URL. */
  filters: MyStudiesFilters;
  /** Texto do campo de busca como digitado; vira `filters.busca` 450 ms depois da última tecla. */
  searchDraft: string;
  /**
   * Muda filtros, com `replace`. Uma mudança com `busca` espera o debounce inteira (a menos que
   * venha com `{ immediate: true }`); as outras valem na hora e levam junto a busca pendente, numa
   * consulta só. Só `page` equivale a `setPage`. Página inválida é ignorada; intervalo invertido é
   * ordenado (ver `applyFilterChange`).
   */
  setFilters: (change: MyStudiesFiltersChange, options?: SetFiltersOptions) => void;
  /**
   * Troca de página: entra no histórico (`push`, D21), a menos que leve junto uma busca pendente
   * (aí é mudança de filtro, `replace`). Página que não é inteiro >= 1 é ignorada.
   */
  setPage: (page: number) => void;
  /**
   * Corrige a página (ex.: além da última, depois de excluir o único estudo dela; #1999) com
   * `replace`, sem entrada nova no histórico (D21: normalização). Não leva a busca pendente: ela
   * segue no debounce e, quando valer, volta para a página 1. Página que não é inteiro >= 1 é
   * ignorada.
   */
  normalizePage: (page: number) => void;
  /** "Limpar filtros" (D6), descartando a busca ainda não aplicada. */
  clearFilters: () => void;
  /**
   * "Limpar filtros" mudaria algo, olhando a busca como DIGITADA (`searchDraft`), não a aplicada:
   * é o mesmo que `buildFilterChips({ ...filters, busca: searchDraft }).length > 0`, então o
   * botão aparece e some junto com os chips do resumo (#1997).
   */
  hasActiveFilters: boolean;
}

function toNamedCreator(value: unknown): NamedCreator | null {
  if (!value || typeof value !== 'object') return null;
  const { userId, nome } = value as Record<string, unknown>;
  if (typeof userId !== 'number' || !isValidUserId(userId)) return null;
  if (typeof nome !== 'string' || nome.trim() === '') return null;
  return { userId, nome };
}

/** Lê a sessão; qualquer falha (bloqueio, JSON inválido, formato errado) vale como "não há". */
function readStoredFilters(userId: number, prefix: string): StoredFilters | null {
  if (!isValidUserId(userId)) return null;
  try {
    const raw = window.sessionStorage.getItem(myStudiesFiltersStorageKey(userId, prefix));
    if (!raw) return null;
    const value: unknown = JSON.parse(raw);
    if (!value || typeof value !== 'object') return null;
    const { query, creator } = value as Record<string, unknown>;
    if (typeof query !== 'string') return null;
    return { query, creator: toNamedCreator(creator) };
  } catch {
    return null;
  }
}

function writeStoredFilters(userId: number, stored: StoredFilters, prefix: string): void {
  if (!isValidUserId(userId)) return;
  try {
    window.sessionStorage.setItem(
      myStudiesFiltersStorageKey(userId, prefix),
      JSON.stringify(stored),
    );
  } catch {
    // Sessão indisponível (modo privado, cota, bloqueio): a URL continua sendo a fonte
  }
}

/**
 * `criado_por=<o próprio id>` é "Meus estudos": o filtro Usuário não lista quem está logado
 * (#2000), e o botão mostraria "Usuário: <eu>". A URL normalizada passa a `criado_por=me`.
 */
function withOwnCreatorAsMe(filters: MyStudiesFilters, userId: number): MyStudiesFilters {
  const { criadoPor } = filters;
  if (typeof criadoPor !== 'object' || criadoPor.userId !== userId) return filters;
  return { ...filters, criadoPor: 'me' };
}

/** URL > sessão > padrão do papel. */
function resolveFilters(
  search: string,
  userId: number,
  isAdmin: boolean,
  todaySP: DateOnly,
  options: ParseFiltersOptions,
  storagePrefix: string,
): MyStudiesFilters {
  const params = new URLSearchParams(search);
  if (hasFilterParams(params)) {
    return withOwnCreatorAsMe(parseFilters(params, isAdmin, todaySP, options), userId);
  }

  const stored = readStoredFilters(userId, storagePrefix);
  if (stored) {
    return withOwnCreatorAsMe(
      parseFilters(new URLSearchParams(stored.query), isAdmin, todaySP, options),
      userId,
    );
  }

  return defaultFilters(isAdmin, todaySP);
}

/** Devolve o nome do criador quando o id da URL é o de um criador conhecido (o 1º que bater). */
function withCreatorName(
  filters: MyStudiesFilters,
  known: ReadonlyArray<NamedCreator | null>,
): MyStudiesFilters {
  const { criadoPor } = filters;
  if (typeof criadoPor !== 'object') return filters;
  const creator = known.find((candidate) => candidate?.userId === criadoPor.userId);
  return creator ? { ...filters, criadoPor: { ...creator } } : filters;
}

/** Search completo: os filtros na forma canônica e, depois, os parâmetros que não são deles. */
function buildSearch(currentSearch: string, filtersQuery: string): string {
  const foreign = new URLSearchParams(currentSearch);
  MY_STUDIES_FILTER_PARAMS.forEach((key) => foreign.delete(key));
  const query = [filtersQuery, foreign.toString()].filter(Boolean).join('&');
  return query ? `?${query}` : '';
}

/**
 * Estado dos filtros da aba "Meus Estudos" (Flux Personalizado, #1990), com a URL como fonte.
 *
 * - Leitura: URL > sessionStorage (`flux-meus-estudos-filtros-v1:<userId>`, ou o
 *   `storagePrefix` do produto) > padrão do papel. A
 *   restauração já vale no 1º render, sem consulta com o filtro errado. `criado_por=<o próprio
 *   id>` vira "Meus estudos" (`'me'`), e a URL é normalizada com `replace` (#2000).
 * - Escrita: URL e sessão. Filtrar, restaurar e normalizar usam `replace`; trocar de página usa
 *   `push` (D21). O `TabbedPage` leva à aba sem a query: é a sessão que mantém os filtros ao voltar
 *   de "Novo estudo".
 * - Busca: debounce de 450 ms (`searchDraft` é o valor do campo). Uma mudança com `busca` espera
 *   inteira; outra mudança com busca pendente a leva junto, numa consulta só, como o timer único
 *   do protótipo (.dc.html:616). Voltar ou avançar no navegador (POP) descarta a busca pendente.
 *   `setFilters(change, { immediate: true })` aplica na hora (remover o chip da busca, #1997).
 *
 * Uma instância por tela: a casca `MyStudies` (#1995) monta a única instância; a #1999 e os
 * componentes da #1997 usam a mesma, por props (`filters`, `searchDraft` e as ações). Nunca monte o
 * hook na view: ele reescreve a URL, e a tela legada (flag desligada) tem de ficar idêntica. Duas
 * instâncias não compartilham a busca pendente: "Limpar filtros" numa não cancela o timer da outra.
 *
 * `userId` e `isAdmin` vêm por parâmetro (o `StudyViewer` das regras da linha): a tela lê
 * `selectUserId`/`selectUserIsAdmin` uma vez e repassa, e o hook fica testável sem store nem JWT.
 * Sem usuário válido (`selectUserId` devolve -1 sem JWT), a sessão não é usada e a URL não é
 * reescrita; quando o login chega, a sessão do usuário é restaurada. O papel (`isAdmin`) precisa
 * estar certo desde o 1º render com usuário válido: a URL normalizada com os padrões do papel
 * errado passaria a valer (a URL vence a sessão e o padrão).
 *
 * `createdRefAvailable` (padrão `true`) vem da casca, a mesma fonte do `PeriodFilter`: com
 * `false` (D8, até a #2006), `ref=created` da URL ou da sessão vira `last_exec` na leitura, e a
 * normalização reescreve a URL e a sessão com `replace`.
 *
 * @param options - Quem vê a tela e se a referência de criação está liberada.
 * @returns Filtros aplicados, valor do campo de busca e as ações.
 */
export default function useMyStudiesFilters(
  {
    userId, isAdmin, createdRefAvailable = true, storagePrefix = STORAGE_PREFIX,
  }: UseMyStudiesFiltersOptions,
): UseMyStudiesFiltersResult {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  // Lido a cada render: os padrões ("1º dia do mês até hoje") não envelhecem depois da meia-noite
  const today = todayInSaoPaulo(new Date());

  // Nome do criador: o guardado na sessão deste usuário (lido de novo se o login chegar depois) e
  // o escolhido nesta tela; a URL só leva o id
  const sessionCreator = useMemo(
    () => readStoredFilters(userId, storagePrefix)?.creator ?? null,
    [userId, storagePrefix],
  );
  const [chosenCreator, setChosenCreator] = useState<NamedCreator | null>(null);

  const resolved = useMemo(
    () => resolveFilters(
      location.search,
      userId,
      isAdmin,
      today,
      { createdRefAvailable },
      storagePrefix,
    ),
    [location.search, userId, isAdmin, today, createdRefAvailable, storagePrefix],
  );
  const filters = useMemo(
    () => withCreatorName(resolved, [chosenCreator, sessionCreator]),
    [resolved, chosenCreator, sessionCreator],
  );
  const filtersQuery = useMemo(() => serializeFilters(resolved), [resolved]);
  const targetSearch = buildSearch(location.search, filtersQuery);
  const { criadoPor } = filters;
  const creatorToStore = typeof criadoPor === 'object' && criadoPor.nome
    ? criadoPor as NamedCreator : null;

  const [searchDraft, setSearchDraft] = useState(filters.busca);
  const pendingSearch = useRef<PendingSearch | null>(null);

  const latest = useRef<LatestState>({ filters, location, navigate });
  useLayoutEffect(() => {
    latest.current = { filters, location, navigate };
  });

  /** Cancela o timer da busca e devolve a mudança que ele aplicaria (ou `null`). */
  const takePendingSearch = useCallback((): MyStudiesFiltersChange | null => {
    const pending = pendingSearch.current;
    if (pending === null) return null;
    clearTimeout(pending.timer);
    pendingSearch.current = null;
    return pending.change;
  }, []);

  // Restaurar ou normalizar: a URL sempre espelha os filtros, sem entrada nova no histórico. Sem
  // usuário válido, espera o login: gravar o padrão agora faria a URL vencer a sessão dele depois.
  useEffect(() => {
    if (!isValidUserId(userId) || targetSearch === location.search) return;
    navigate(
      { pathname: location.pathname, search: targetSearch, hash: location.hash },
      { replace: true, state: location.state },
    );
  }, [userId, targetSearch, location, navigate]);

  useEffect(() => {
    writeStoredFilters(userId, { query: filtersQuery, creator: creatorToStore }, storagePrefix);
  }, [userId, filtersQuery, creatorToStore, storagePrefix]);

  // Voltar ou avançar (POP) descarta a busca pendente: ela era da entrada de onde o usuário saiu.
  // Sem busca pendente, o campo acompanha a busca que mudou por fora (voltar, link); só espaço nas
  // pontas não conta, para o espaço digitado no fim não sumir quando a busca é aplicada.
  useEffect(() => {
    if (navigationType === 'POP') takePendingSearch();
    if (pendingSearch.current !== null) return;
    setSearchDraft((draft) => (draft.trim() === filters.busca ? draft : filters.busca));
  }, [location.key, navigationType, filters.busca, takePendingSearch]);

  // Sair da tela com busca pendente: o timer não pode navegar de volta para ela
  useEffect(() => () => { takePendingSearch(); }, [takePendingSearch]);

  const commit = useCallback((next: MyStudiesFilters, mode: HistoryMode) => {
    const { current } = latest;
    current.filters = next;

    const { criadoPor: nextCreator } = next;
    if (typeof nextCreator === 'object' && nextCreator.nome) {
      setChosenCreator({ userId: nextCreator.userId, nome: nextCreator.nome });
    }

    const search = buildSearch(current.location.search, serializeFilters(next));
    if (search === current.location.search) return;
    current.location = { ...current.location, search };
    current.navigate(
      { pathname: current.location.pathname, search, hash: current.location.hash },
      { replace: mode === 'replace', state: current.location.state },
    );
  }, []);

  const setFilters = useCallback((change: MyStudiesFiltersChange, options?: SetFiltersOptions) => {
    // `?.` e `=== true`: opções nulas ou de outro tipo (chamada sem tipo) valem como "sem opção"
    const immediate = options?.immediate === true;
    const sanitized = sanitizeFilterChange(change);
    if (Object.keys(sanitized).length === 0) return;

    if (sanitized.busca !== undefined && !immediate) {
      // A mudança inteira espera a última tecla, somada à que já esperava
      const merged = { ...(takePendingSearch() ?? {}), ...sanitized };
      setSearchDraft(sanitized.busca);
      const timer = setTimeout(() => {
        pendingSearch.current = null;
        commit(applyFilterChange(latest.current.filters, merged), 'replace');
      }, SEARCH_DEBOUNCE_MS);
      pendingSearch.current = { change: merged, timer };
      return;
    }

    // Outra mudança com busca pendente: vai junto, numa consulta só (a busca imediata a substitui)
    if (sanitized.busca !== undefined) setSearchDraft(sanitized.busca);
    const full = { ...(takePendingSearch() ?? {}), ...sanitized };
    const onlyPage = Object.keys(full).length === 1 && full.page !== undefined;
    commit(applyFilterChange(latest.current.filters, full), onlyPage ? 'push' : 'replace');
  }, [takePendingSearch, commit]);

  const setPage = useCallback((page: number) => setFilters({ page }), [setFilters]);

  // Sem `takePendingSearch`: a correção usa os filtros aplicados e não adianta a busca digitada
  const normalizePage = useCallback((page: number) => {
    commit(applyFilterChange(latest.current.filters, { page }), 'replace');
  }, [commit]);

  const clearFilters = useCallback(() => {
    takePendingSearch();
    setSearchDraft('');
    commit(clearedFilters(latest.current.filters, todayInSaoPaulo(new Date())), 'replace');
  }, [takePendingSearch, commit]);

  const hasActiveFilters = useMemo(
    () => filtersAreActive({ ...filters, busca: searchDraft }),
    [filters, searchDraft],
  );

  return {
    filters,
    searchDraft,
    setFilters,
    setPage,
    normalizePage,
    clearFilters,
    hasActiveFilters,
  };
}
